import React, { useState, useEffect } from 'react';

const AnalysisPage = ({ categories, menuItems, sales }) => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [analyzedProducts, setAnalyzedProducts] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const safeNumber = (value) => {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  const loadRecipes = async () => {
    try {
      const response = await fetch("/api/list_recipes.php?all=true");
      const result = await response.json();
      if (result.success) {
        const cleanRecipes = (result.recipes || result.data || []).map(r => ({
          ...r,
          recipeCostPercentage: safeNumber(r.recipeCostPercentage ?? r.recipe_cost_percentage),
        }));
        setRecipes(cleanRecipes);
      } else {
        console.error("Error al cargar recetas:", result.error);
      }
    } catch (error) {
      console.error("Error de conexión al cargar recetas:", error);
    }
  };

  useEffect(() => { loadRecipes(); }, []);

  useEffect(() => {
    if (Array.isArray(menuItems) && Array.isArray(sales) && Array.isArray(recipes)) {

      const filteredSales = sales.filter(sale => {
        if (!startDate && !endDate) return true;
        const saleDate = new Date(sale.date || sale.sale_date);
        if (startDate && endDate) return saleDate >= new Date(startDate) && saleDate <= new Date(endDate);
        if (startDate) return saleDate >= new Date(startDate);
        if (endDate) return saleDate <= new Date(endDate);
        return true;
      });

      const productsInCategory = selectedCategory
        ? menuItems.filter(item =>
            item &&
            (item.categoryId?.toString() === selectedCategory ||
             item.category_id?.toString() === selectedCategory)
          )
        : menuItems;

      const totalSalesInCategory = filteredSales.reduce((total, sale) => {
        const productId = sale?.product_id ?? sale?.id;
        if (!productId) return total;
        const product = menuItems.find(item => item?.id?.toString() === productId.toString());
        const productCategory = product?.categoryId ?? product?.category_id ?? null;
        if (!selectedCategory || (productCategory?.toString() === selectedCategory)) {
          return total + safeNumber(sale.quantity || sale.quantitySold);
        }
        return total;
      }, 0);

      const recipesWithCost = recipes.filter(
        rec => rec && rec.recipeCostPercentage !== undefined && rec.recipeCostPercentage !== null
      );
      const totalCostPercentages = recipesWithCost.reduce(
        (sum, recipe) => sum + safeNumber(recipe.recipeCostPercentage), 0
      );
      const averageCostPercentage = recipesWithCost.length > 0
        ? totalCostPercentages / recipesWithCost.length
        : 0;

      const analyzed = productsInCategory.map(product => {
        if (!product || !product.id) return null;

        const quantitySold = filteredSales
          .filter(sale => {
            const productId = sale?.product_id ?? sale?.id;
            if (!productId) return false;
            return productId.toString() === product.id.toString();
          })
          .reduce((total, sale) => total + safeNumber(sale.quantity || sale.quantitySold), 0);

        const recipe = recipes.find(
          rec => rec &&
            (rec.productId?.toString() === product.id.toString() ||
             rec.product_id?.toString() === product.id.toString())
        );

        const costPercentage   = safeNumber(recipe ? recipe.recipeCostPercentage : 0);
        const popularityPercentage = totalSalesInCategory > 0
          ? (quantitySold / totalSalesInCategory) * 100
          : 0;
        const isHighPopularity = quantitySold > productsInCategory.length / 2;
        const isHighCost       = costPercentage > averageCostPercentage;

        let classification = 'Normal';
        if      (isHighPopularity && !isHighCost) classification = 'Estrella';
        else if (isHighPopularity && isHighCost)  classification = 'Caballo de Batalla';
        else if (!isHighPopularity && !isHighCost) classification = 'Rompecabezas';
        else if (!isHighPopularity && isHighCost)  classification = 'Perro';

        return { ...product, quantitySold, popularityPercentage, costPercentage, classification };
      }).filter(Boolean);

      setAnalyzedProducts(analyzed);
    } else {
      setAnalyzedProducts([]);
    }
  }, [selectedCategory, menuItems, sales, recipes, startDate, endDate]);

  const handleCategoryChange = (e) => setSelectedCategory(e.target.value);

  // FIX: configuración de clasificaciones para la leyenda
  const classifications = [
    {
      icon: '⭐',
      label: 'Estrella',
      color: '#58735A',
      bgColor: '#EAF3DE',
      borderColor: '#58735A',
      title: 'Alta popularidad · Bajo costo',
      description: 'Tu producto más valioso. Se vende mucho y su costo es controlado. Protégelo, destacalo en el menú y asegúrate de que siempre esté disponible.'
    },
    {
      icon: '🐎',
      label: 'Caballo de Batalla',
      color: '#3F1907',
      bgColor: '#F9EFD7',
      borderColor: '#3F1907',
      title: 'Alta popularidad · Alto costo',
      description: 'Se vende bien pero su costo es elevado. Revisa si puedes optimizar la receta o ajustar el precio sin afectar la demanda.'
    },
    {
      icon: '🧩',
      label: 'Rompecabezas',
      color: '#B45309',
      bgColor: '#FAEEDA',
      borderColor: '#B45309',
      title: 'Baja popularidad · Bajo costo',
      description: 'Tiene potencial pero no se vende suficiente. Considera promocionarlo más o reposicionarlo en el menú para aumentar su visibilidad.'
    },
    {
      icon: '🐶',
      label: 'Perro',
      color: '#991B1B',
      bgColor: '#FEE2E2',
      borderColor: '#991B1B',
      title: 'Baja popularidad · Alto costo',
      description: 'Poco vendido y costoso de producir. Evalúa si vale la pena mantenerlo en el menú o si es mejor retirarlo o reformularlo.'
    }
  ];

  // FIX: color por clasificación en la tabla
  const getClassificationStyle = (classification) => {
    switch (classification) {
      case 'Estrella':          return { color: '#58735A',  fontWeight: '600' };
      case 'Caballo de Batalla': return { color: '#3F1907',  fontWeight: '600' };
      case 'Rompecabezas':      return { color: '#B45309',  fontWeight: '600' };
      case 'Perro':             return { color: '#991B1B',  fontWeight: '600' };
      default:                  return { color: '#3F1907' };
    }
  };

  const getClassificationIcon = (classification) => {
    switch (classification) {
      case 'Estrella':           return '⭐';
      case 'Caballo de Batalla': return '🐎';
      case 'Rompecabezas':       return '🧩';
      case 'Perro':              return '🐶';
      default:                   return '';
    }
  };

  return (
    <div className="mt-6">
      <div className="bg-[#F9EFD7] p-4 rounded-lg shadow mb-6">
        <h3 className="text-lg font-semibold text-[#3F1907] mb-3">Análisis del Menú</h3>

        {/* FILTROS */}
        <div className="mb-4">
          <label className="block text-[#3F1907] text-sm font-bold mb-1">Seleccionar Categoría</label>
          <select
            value={selectedCategory}
            onChange={handleCategoryChange}
            className="w-full px-3 py-2 border border-[#58735A] rounded-md focus:outline-none focus:ring-2 focus:ring-[#3F1907] bg-[#F9EFD7]"
          >
            <option value="">Todas las Categorías</option>
            {categories.map(category => (
              <option key={category.id} value={category.id}>{category.name}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-[#3F1907] text-sm font-bold mb-1">Desde</label>
            <input
              type="date" value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-[#58735A] rounded-md bg-[#F9EFD7]"
            />
          </div>
          <div>
            <label className="block text-[#3F1907] text-sm font-bold mb-1">Hasta</label>
            <input
              type="date" value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-[#58735A] rounded-md bg-[#F9EFD7]"
            />
          </div>
        </div>

        {/* TABLA */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#58735A]">
            <thead className="bg-[#58735A]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#F9EFD7] uppercase tracking-wider">Producto</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#F9EFD7] uppercase tracking-wider">Cantidad Vendida</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#F9EFD7] uppercase tracking-wider">Popularidad %</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#F9EFD7] uppercase tracking-wider">Costo %</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#F9EFD7] uppercase tracking-wider">Clasificación</th>
              </tr>
            </thead>
            <tbody className="bg-[#F9EFD7] divide-y divide-[#58735A]">
              {analyzedProducts.map(product => (
                <tr key={product.id}>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-[#3F1907]">{product.name}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-[#3F1907]">{product.quantitySold}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-[#3F1907]">
                    {safeNumber(product.popularityPercentage).toFixed(2)}%
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-[#3F1907]">
                    {safeNumber(product.costPercentage).toFixed(2)}%
                  </td>
                  {/* FIX: clasificación con icono y color */}
                  <td className="px-4 py-3 whitespace-nowrap text-sm" style={getClassificationStyle(product.classification)}>
                    {getClassificationIcon(product.classification)} {product.classification}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* FIX: leyenda de clasificaciones */}
      <div className="bg-[#F9EFD7] p-4 rounded-lg shadow">
        <h4 className="text-md font-semibold text-[#3F1907] mb-4">
          ¿Qué significa cada clasificación?
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {classifications.map(c => (
            <div
              key={c.label}
              style={{
                background:   c.bgColor,
                border:       `1.5px solid ${c.borderColor}`,
                borderRadius: '10px',
                padding:      '16px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                <span style={{ fontSize: '24px' }}>{c.icon}</span>
                <div>
                  <p style={{ margin: 0, fontWeight: '600', fontSize: '15px', color: c.color }}>
                    {c.label}
                  </p>
                  <p style={{ margin: 0, fontSize: '11px', color: c.color, opacity: 0.75 }}>
                    {c.title}
                  </p>
                </div>
              </div>
              <p style={{ margin: 0, fontSize: '13px', color: '#3F1907', lineHeight: '1.5' }}>
                {c.description}
              </p>
            </div>
          ))}
        </div>

        <p style={{
          marginTop:  '16px',
          fontSize:   '12px',
          color:      '#888',
          textAlign:  'center',
          lineHeight: '1.6'
        }}>
          La clasificación se basa en la metodología de Ingeniería de Menú de Miller & Kasavana.
          La popularidad se mide contra el promedio de ventas del grupo y el costo contra el promedio de costo de las recetas registradas.
        </p>
      </div>
    </div>
  );
};

export default AnalysisPage;